import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    Box, Button, IconButton, LinearProgress, MenuItem, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LockResetIcon from '@mui/icons-material/LockReset'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import { H3 } from '../../../layouts/TextStyles'
import { tableStyles, ui } from '../../../utils/uiTokens'
import { schoolApiSlice, SchoolStudent } from '../../../features/school/schoolSlice'
import ConfirmActionDialog from '../../../components/dialogs/ConfirmActionDialog'
import { Content, Header, Toolbar, Wrapper } from '../../admin/mentorSalaryPage/styles'
import { titleOf } from '../titleOf'
import AddStudentDialog from './AddStudentDialog'
import ImportStudentsDialog from './ImportStudentsDialog'
import EditStudentDialog from './EditStudentDialog'
import { downloadFile } from './downloadFile'

const PAGE_SIZE = 50
const GRADES = Array.from({ length: 12 }, (_, i) => i + 1)

// Ученики школы. Админ школы видит свою (schoolId = 0 -> школа из привязки),
// модератор открывает любую через /schools/:schoolId/students.
const SchoolStudentsPage: React.FC = () => {
    const { formatMessage, locale } = useIntl()
    const params = useParams()
    const schoolId = Number(params.schoolId) || 0

    const [grade, setGrade] = useState(0)
    const [literal, setLiteral] = useState('')
    const [search, setSearch] = useState('')
    const [query, setQuery] = useState('')
    const [page, setPage] = useState(0)
    const [dialog, setDialog] = useState<'add' | 'import' | null>(null)
    const [editing, setEditing] = useState<SchoolStudent | null>(null)
    const [detaching, setDetaching] = useState<SchoolStudent | null>(null)

    const { data: mySchools = [] } = schoolApiSlice.useGetMySchoolsQuery(undefined, { skip: !!schoolId })
    const { data, isFetching } = schoolApiSlice.useGetSchoolStudentsQuery({
        schoolId, grade, literal, query, page, size: PAGE_SIZE,
    })
    const [resetPassword] = schoolApiSlice.useResetSchoolStudentPasswordMutation()
    const [detach, { isLoading: detachingNow }] = schoolApiSlice.useDetachSchoolStudentMutation()

    useEffect(() => {
        const timer = setTimeout(() => {
            setQuery(search.trim())
            setPage(0)
        }, 300)
        return () => clearTimeout(timer)
    }, [search])

    const school = mySchools[0]
    const title = schoolId ? formatMessage({ id: 'schoolStudents' }) : school ? titleOf(school, locale) : formatMessage({ id: 'mySchool' })

    const handleReset = async (s: SchoolStudent) => {
        try {
            const res = await resetPassword(s.id).unwrap()
            toast.success(`${res.lastName} ${res.firstName}: ${res.tempPassword}`)
        } catch { /* общий обработчик */ }
    }

    const handleDetach = async () => {
        if (!detaching) return
        try {
            await detach(detaching.id).unwrap()
            setDetaching(null)
        } catch { /* общий обработчик */ }
    }

    const handleLogins = async () => {
        const qs = new URLSearchParams({ schoolId: String(schoolId), grade: String(grade), literal })
        const ok = await downloadFile(`school/students/credentials?${qs}`, 'students-logins.xlsx')
        if (!ok) toast.error(formatMessage({ id: 'schoolDownloadFailed' }))
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Box>
                        <H3>{title}</H3>
                        {!schoolId && school && (
                            <Box sx={{ color: ui.sub, fontSize: '13.5px', mt: '6px' }}>
                                {[titleOf({ titleKz: school.regionTitleKz, titleRu: school.regionTitleRu }, locale),
                                    school.districtId ? titleOf({ titleKz: school.districtTitleKz, titleRu: school.districtTitleRu }, locale) : null]
                                    .filter(Boolean).join(' · ')}
                                {' · '}{formatMessage({ id: 'schoolStudentCount' }, { count: data?.totalCount ?? 0 })}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={handleLogins}>{formatMessage({ id: 'schoolDownloadLogins' })}</Button>
                        <Button variant="outlined" onClick={() => setDialog('import')}>{formatMessage({ id: 'schoolImport' })}</Button>
                        <Button variant="contained" onClick={() => setDialog('add')}>{formatMessage({ id: 'schoolAddStudent' })}</Button>
                    </Box>
                </Header>

                <Toolbar>
                    <Select size="small" value={grade} onChange={(e) => { setGrade(Number(e.target.value)); setPage(0) }} sx={selectSx}>
                        <MenuItem value={0}>{formatMessage({ id: 'schoolAllGrades' })}</MenuItem>
                        {GRADES.map((g) => <MenuItem key={g} value={g}>{g} {formatMessage({ id: 'grade' }).toLowerCase()}</MenuItem>)}
                    </Select>
                    <TextField size="small" placeholder={formatMessage({ id: 'schoolLiteral' })} value={literal}
                        inputProps={{ maxLength: 1 }} sx={{ width: 90 }}
                        onChange={(e) => { setLiteral(e.target.value); setPage(0) }} />
                    <TextField size="small" placeholder={formatMessage({ id: 'search' })} value={search}
                        onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
                </Toolbar>

                {isFetching && <LinearProgress />}
                <TableContainer sx={tableStyles.container}>
                    <Table>
                        <TableHead sx={tableStyles.head}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'fullName' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'grade' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'schoolLogin' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'temporaryPassword' })}</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.data ?? []).map((s) => (
                                <TableRow key={s.id} sx={tableStyles.row}>
                                    <TableCell sx={{ fontWeight: 600 }}>{s.lastName} {s.firstName}</TableCell>
                                    <TableCell>{s.grade ? `${s.grade}${s.literal ?? ''}` : '-'}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{s.username ?? s.mobile ?? '-'}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                        {s.tempPassword ?? <Box component="span" sx={{ color: ui.muted, fontFamily: 'inherit' }}>{formatMessage({ id: 'schoolPasswordChanged' })}</Box>}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title={formatMessage({ id: 'edit' })}>
                                            <IconButton size="small" onClick={() => setEditing(s)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={formatMessage({ id: 'schoolResetPassword' })}>
                                            <IconButton size="small" onClick={() => handleReset(s)}><LockResetIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={formatMessage({ id: 'schoolDetach' })}>
                                            <IconButton size="small" onClick={() => setDetaching(s)}><PersonRemoveOutlinedIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isFetching && (data?.data ?? []).length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: ui.muted, py: 4 }}>
                                        {formatMessage({ id: 'schoolNoStudents' })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data?.totalCount ?? 0}
                    page={page}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    onPageChange={(e, p) => setPage(p)}
                />
            </Content>

            {dialog === 'add' && <AddStudentDialog schoolId={schoolId} onClose={() => setDialog(null)} />}
            {dialog === 'import' && <ImportStudentsDialog schoolId={schoolId} onClose={() => setDialog(null)} />}
            {editing && <EditStudentDialog student={editing} onClose={() => setEditing(null)} />}
            <ConfirmActionDialog
                open={!!detaching}
                setOpen={(open) => !open && setDetaching(null)}
                confirmAction={handleDetach}
                loading={detachingNow}
                contentText={formatMessage({ id: 'schoolDetachConfirm' })}
            />
        </Wrapper>
    )
}

const selectSx = {
    minWidth: 150,
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
}

export default SchoolStudentsPage
